import { memo, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useGlobalEvent } from '@highlight/ipc/react'
import * as Sentry from '@sentry/electron/renderer'

import { useGatewayChannel } from '@renderer/hooks/useGatewayChannel'
import { useGatewayConnect } from '@renderer/overlay/hooks/use-gateway-connect'
import {
  CHAT_HISTORY_QUERY_KEY,
  chatQueryKey,
  FOR_YOU_CHATS_QUERY_KEY,
  HISTORY_ACTIVITY_QUERY_KEY,
  HISTORY_PREVIEW_QUERY_KEY,
} from '@renderer/stores/conversation-query-keys'
import {
  applyConversationPreviewAcknowledgement,
  type ConversationPreviewDisposition,
} from '@renderer/overlay/hooks/conversation-preview-cache'
import { forgetPreviewAcknowledgement } from '@renderer/overlay/hooks/conversation-preview-acknowledgements'
import type { SidepanelChat } from '@renderer/overlay/hooks/running-chat-selection'
import { STAGED_ACTIONS_QUERY_KEY } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { REMINDERS_QUERY_KEY } from '@renderer/overlay/Sidepanel/hooks/use-reminders'

/**
 * Tracing the live path end to end: gateway message in, refetch out.
 *
 * Unconditional rather than dev-gated on purpose — the thing worth watching is
 * whether events arrive in a real build against a real gateway, which is
 * exactly where a `import.meta.env.DEV` guard would go quiet. Drop or gate
 * these once the path is confirmed.
 */
const log = (message: string, detail?: unknown) => {
  if (detail === undefined) {
    console.log(`[conversations-gateway] ${message}`)
  } else {
    console.log(`[conversations-gateway] ${message}`, detail)
  }
}

export const ConversationsGatewayProvider = memo(({ children }: React.PropsWithChildren) => {
  const queryClient = useQueryClient()

  useGlobalEvent(
    'conversation-preview:acknowledged',
    useCallback(
      (chat: SidepanelChat, disposition: ConversationPreviewDisposition) => {
        applyConversationPreviewAcknowledgement(queryClient, chat, disposition)
      },
      [queryClient],
    ),
  )
  useGlobalEvent(
    'conversation-preview:acknowledgement-reverted',
    useCallback(
      (conversationId: string, revision: number) => {
        forgetPreviewAcknowledgement(conversationId, revision)
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: [FOR_YOU_CHATS_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [HISTORY_ACTIVITY_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [REMINDERS_QUERY_KEY] }),
        ]).catch((error) => Sentry.captureException(error))
      },
      [queryClient],
    ),
  )

  const invalidateConversationQueries = useCallback(
    async (conversationId?: string): Promise<void> => {
      const startedAt = performance.now()
      log('invalidating conversation queries', {
        conversationId: conversationId ?? '(all)',
        keys: [FOR_YOU_CHATS_QUERY_KEY, HISTORY_ACTIVITY_QUERY_KEY, CHAT_HISTORY_QUERY_KEY, HISTORY_PREVIEW_QUERY_KEY],
      })
      const invalidations = [
        queryClient.invalidateQueries({ queryKey: [FOR_YOU_CHATS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [HISTORY_ACTIVITY_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [CHAT_HISTORY_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [HISTORY_PREVIEW_QUERY_KEY] }),
      ]

      if (conversationId) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: chatQueryKey(conversationId) }),
          queryClient.invalidateQueries({ queryKey: ['conversation-streaming-state', conversationId] }),
        )
      }

      await Promise.all(invalidations)
      log(`invalidation settled in ${Math.round(performance.now() - startedAt)}ms`)
    },
    [queryClient],
  )

  const invalidateSafely = useCallback(
    (conversationId?: string) => {
      void invalidateConversationQueries(conversationId).catch((error) => {
        log('invalidation failed', error)
        Sentry.captureException(error)
      })
    },
    [invalidateConversationQueries],
  )

  useGatewayChannel('conversations', (payload) => {
    // The whole payload rather than picked fields: `changed` only exists on the
    // 'changed' action, and this is the line that answers "did the event even
    // arrive, and what did it say".
    log('channel message', payload)
    invalidateSafely(payload.conversationId)
  })

  // No conversation id: a reconnect reconciles the whole feed, not one row.
  useGatewayConnect(() => {
    log('gateway connected — reconciling')
    return invalidateConversationQueries()
  })

  return <>{children}</>
})
ConversationsGatewayProvider.displayName = 'ConversationsGatewayProvider'
